// LoginForm.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Select from "@radix-ui/react-select";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Globe } from "lucide-react";
import { useAccountStore } from "../../store/useAccountStore";
import { api } from "../../api";
import { toast } from "react-toastify";
import { useCountryStore } from "../../store/useCountryStore";
const EyeCloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 text-gray-700"
  >
    <path d="M3 3L21 21" />
    <path d="M10.584 10.586A3 3 0 0013.414 13.414M9.879 4.267C10.572 4.09 11.285 4 12 4c5 0 9.27 3.11 10.5 8-0.46 1.73-1.33 3.3-2.5 4.58M6.529 6.527C4.44 7.974 2.93 10.164 2.5 12c0.461 1.735 1.332 3.303 2.503 4.585C6.435 18.928 9.077 20 12 20c1.724 0 3.389-0.453 4.826-1.267" />
  </svg>
);

const companies = [
  {
    label: "Bangladesh-Pureit",
    value: "pureit-bangladesh",
    flag: "assets/bangladesh-flag.svg",
    company: "pureit",
    country: "BD",
    domain: "@aosmith.com",
  },
  {
    label: "India-AOSmith",
    value: "aosmith-india",
    flag: "assets/india-flag.svg",
    company: "aosmith",
    country: "IN",
    domain: "@aosmithindia.com",
  },

  {
    label: "India-Pureit",
    value: "pureit-india",
    flag: "assets/india-flag.svg",
    company: "pureit",
    country: "IN",
    domain: "@aosmith.com",
  },

  {
    label: "Sri Lanka-Pureit",
    value: "pureit-srilanka",
    flag: "assets/srilanka-flag.svg",
    company: "pureit",
    country: "LK",
    domain: "@aosmith.com",
  },
];

// Sort companies alphabetically
const sortedCompanies = [...companies].sort((a, b) =>
  a.label.localeCompare(b.label)
);

const LoginForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [companySelection, setCompanySelection] = useState<string | undefined>(
    undefined
  );
  const { setUser } = useAccountStore();
  const navigate = useNavigate();

  // Extract company and country from selection
  const getCompanyAndCountry = (selection: string) => {
    const selectedCompany = companies.find((c) => c.value === selection);
    if (selectedCompany) {
      return {
        company: selectedCompany.value,
        country: selectedCompany.country,
        domain: selectedCompany.domain,
      };
    }
    return { company: "aosmith", country: "IN", domain: "@aosmith.com" }; // fallback
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!username.trim()) {
      toast.error("Username is required.");
      return;
    }
    if (!password.trim()) {
      toast.error("Password is required.");
      return;
    }
    if (!companySelection) {
      toast.error("Please select a company.");
      return;
    }

    const { company, country, domain } = getCompanyAndCountry(companySelection);
    console.log("Logging in with:", { username, company, country });

    api
      .post("/users/login", {
        username: username,
        password,
        country: country,
      })
      .then((response: any) => {
        console.log("Login Response:", response.data);
        if (response.data?.token) {
          toast.success(response.data?.message || "Login successful.");
          const userEmail = response.data?.email || username;
          console.log("Setting user with email:", userEmail);

          // Use country from API response if available, otherwise derive from selection
          const finalCountry = country;

          setUser({
            name: username,
            country: finalCountry,
            company: company,
            email: userEmail, // Use email from response, fallback to username
            id: username,
            token: response.data?.token,
            username: response.data?.username || username,
            password,
          });
          useCountryStore.getState().setCountry({
            id: finalCountry,
            name:
              finalCountry === "IN"
                ? "India"
                : finalCountry === "BD"
                ? "Bangladesh"
                : "Sri Lanka",
            flag:
              finalCountry === "IN"
                ? "assets/india-flag.svg"
                : finalCountry === "BD"
                ? "assets/bangladesh-flag.svg"
                : "assets/srilanka-flag.svg",
          });
          navigate("/select-dashboard");
          return;
        } else {
          toast.error(
            response?.error?.message || "Login failed. Please try again."
          );
        }
      })
      .catch((error) => {
        console.error("Login failed:", error);
        toast.error(
          "Login failed. Please check your credentials and try again."
        );
      });
  };

  return (
    <div className="w-full max-w-sm mx-auto py-8">
      <div className="space-y-6">
        {/* Username */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <img
              src="assets/login-user.svg"
              alt="user-icon"
              className="h-5 w-5"
            />
          </div>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="block w-full pl-10 pr-4 py-3 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-800 shadow-sm"
          />
        </div>

        {/* Password */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <img
              src="assets/lock-password.svg"
              alt="lock-icon"
              className="h-5 w-5"
            />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="block w-full pl-10 pr-10 py-3 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-800 shadow-sm"
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-500 hover:text-gray-700 p-1"
            >
              {showPassword ? (
                <EyeCloseIcon />
              ) : (
                <img src="assets/eye.svg" alt="eye" className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Company Dropdown */}
        <div className="relative w-full bg-white">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-lg">
              <Globe color="#536778" />
            </span>
          </div>
          <Select.Root
            value={companySelection}
            onValueChange={setCompanySelection}
          >
            <Select.Trigger className="inline-flex items-center justify-between w-full pl-10 pr-4 py-3 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 shadow-sm">
              <Select.Value placeholder="Select Company" />
              <Select.Icon>
                <ChevronDownIcon
                  height={30}
                  width={30}
                  color="#536778"
                  className="mr-[-5px]"
                />
              </Select.Icon>
            </Select.Trigger>

            <Select.Portal>
              <Select.Content className="bg-white border border-gray-300 rounded-md shadow-lg w-full z-50">
                <Select.Viewport>
                  {sortedCompanies.map((c) => (
                    <Select.Item
                      key={c.value}
                      value={c.value}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2"
                    >
                      <img src={c.flag} className="size-4" />
                      <Select.ItemText>{c.label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>

        {/* Login Button */}
        <div>
        <button
          onClick={handleSubmit}
          className="w-full bg-primaryGreen hover:bg-green-700 text-white py-3 rounded-lg transition-colors"
        >
          Login
        </button>
        <p className="text-sm text-gray-500 text-wrap leading-relaxed m-2">
          Use your AO Smith email name (before @) and password to sign in.
        </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
